import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiTooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { getPromosAPI, setPromoStatusAPI, getAdminId } from '../operations/promoService';
import PromoFormDialog from '../components/promo-code/PromoFormDialog';
import {
  Promo,
  formatDiscount,
  formatMoney,
  formatDate,
  formatDateRange,
  extractApiError,
} from '../components/promo-code/types';

const COLS = 11;

type PromoFilters = {
  promo_code: string;
  date_from: string; // "YYYY-MM-DD"
  date_to: string; // "YYYY-MM-DD"
};

const emptyFilters: PromoFilters = { promo_code: '', date_from: '', date_to: '' };

// Only promo_code is a server-side filter (the engine's exact, case-insensitive
// match). The date range has no engine param, so it is applied client-side.
const buildParams = (f: PromoFilters): Record<string, string> => {
  const p: Record<string, string> = {};
  if (f.promo_code.trim()) p.promo_code = f.promo_code.trim();
  return p;
};

// A promo passes the date-range filter when its validity window overlaps the
// selected [from, to] range (either bound is optional).
const overlapsRange = (promo: Promo, from: string, to: string): boolean => {
  if (!from && !to) return true;
  const s = (promo.start_date || '').slice(0, 10);
  const e = (promo.end_date || '').slice(0, 10);
  if (from && e && e < from) return false; // promo ends before the range starts
  if (to && s && s > to) return false; // promo starts after the range ends
  return true;
};

// Audit tooltip fed with the real promo audit fields (the shared common/Tooltip
// is hardcoded, so we render a small local one).
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: { color: '#f7fcff' },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f7fcff',
    color: '#272727',
    maxWidth: 280,
    fontSize: 13,
    border: '1px solid #a4d1f0',
    borderRadius: 8,
    padding: 12,
  },
}));

const AuditTooltip: React.FC<{ promo: Promo }> = ({ promo }) => (
  <HtmlTooltip
    placement="bottom-end"
    title={
      <React.Fragment>
        <div>Created by — {promo.created_by || '—'}</div>
        <div>Created at — {formatDate(promo.created_at)}</div>
        <div>Last edited by — {promo.modified_by || '—'}</div>
        <div>Last edited at — {formatDate(promo.updated_at)}</div>
      </React.Fragment>
    }
  >
    <IconButton size="small">
      <InfoOutlineIcon fontSize="small" />
    </IconButton>
  </HtmlTooltip>
);

const StatusChip: React.FC<{ active: number }> = ({ active }) => (
  <Chip
    size="small"
    label={Number(active) === 1 ? 'Active' : 'Inactive'}
    sx={{
      width: 96,
      backgroundColor: Number(active) === 1 ? '#1cad2d' : '#e31a24',
      color: '#fff',
      fontWeight: 600,
    }}
  />
);

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: '#696969', display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      sx={{ color: '#272727', overflowWrap: 'anywhere' }}
    >
      {value}
    </Typography>
  </Box>
);

// Two-line audit value: who on top, timestamp below.
const AuditValue: React.FC<{ who: string | null; when: string }> = ({ who, when }) => (
  <>
    <Box component="span" sx={{ display: 'block' }}>
      {who || '—'}
    </Box>
    <Box component="span" sx={{ display: 'block', color: '#696969' }}>
      {formatDate(when)}
    </Box>
  </>
);

function PromoRow({
  promo,
  onEdit,
  onToggle,
}: {
  promo: Promo;
  onEdit: (p: Promo) => void;
  onToggle: (p: Promo) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = Number(promo.is_active) === 1;
  return (
    <>
      <TableRow>
        <TableCell>{promo.promo_code}</TableCell>
        <TableCell>{promo.title || '—'}</TableCell>
        <TableCell>{formatDiscount(promo)}</TableCell>
        <TableCell>{formatMoney(promo.min_price)}</TableCell>
        <TableCell>{promo.max_discount == null ? '—' : formatMoney(promo.max_discount)}</TableCell>
        <TableCell>{formatDateRange(promo)}</TableCell>
        <TableCell>
          {promo.promo_usage_count} / {promo.usage_limit}
        </TableCell>
        <TableCell>
          <StatusChip active={promo.is_active} />
        </TableCell>
        <TableCell align="center">
          <Box display="flex" gap={1} justifyContent="center" alignItems="center">
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => onEdit(promo)}
              sx={{ textTransform: 'none' }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => onToggle(promo)}
              sx={{
                minWidth: 118,
                backgroundColor: active ? '#e31a24' : '#1cad2d',
                color: '#fff',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              {active ? 'Deactivate' : 'Activate'}
            </Button>
          </Box>
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" onClick={() => setOpen(!open)} className="viewBtn">
            {open ? (
              <>
                View Less <KeyboardArrowUpIcon />
              </>
            ) : (
              <>
                View More <KeyboardArrowDownIcon />
              </>
            )}
          </IconButton>
        </TableCell>
        <TableCell align="center">
          <AuditTooltip promo={promo} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={COLS} style={{ paddingBottom: 0, paddingTop: 0 }} className="innerTableBG">
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2.5, px: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#272727', mb: 2 }}>
                Details
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 2.5,
                }}
              >
                <DetailField label="Description" value={promo.description || '—'} />
                <DetailField label="Per User Limit" value={promo.per_user_usage_count} />
                <DetailField
                  label="Redeemed"
                  value={`${promo.promo_usage_count} / ${promo.usage_limit}`}
                />
                <DetailField
                  label="Created"
                  value={<AuditValue who={promo.created_by} when={promo.created_at} />}
                />
                <DetailField
                  label="Last Modified"
                  value={<AuditValue who={promo.modified_by} when={promo.updated_at} />}
                />
                <DetailField label="Promo ID" value={promo.promo_id} />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function PromoCode() {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState<PromoFilters>(emptyFilters);
  const [appliedParams, setAppliedParams] = useState<Record<string, string>>({});
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string }>({ from: '', to: '' });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingPromo, setEditingPromo] = useState<Promo | null>(null);

  const [confirmPromo, setConfirmPromo] = useState<Promo | null>(null);
  const [toggling, setToggling] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  });

  const load = useCallback(async (params: Record<string, string> = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPromosAPI(params);
      if (res?.status === 200 && Array.isArray(res.data)) {
        setPromos(res.data as Promo[]);
      } else {
        setPromos([]);
        setError(res?.message || 'Failed to load promos');
      }
    } catch (err: any) {
      setPromos([]);
      setError(err?.response?.data?.message || err?.message || 'Failed to load promos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({});
  }, [load]);

  const onSearch = () => {
    if (filters.date_from && filters.date_to && filters.date_from > filters.date_to) {
      setSnack({ open: true, msg: 'Start date must be on or before end date', sev: 'error' });
      return;
    }
    const params = buildParams(filters);
    setAppliedParams(params);
    setAppliedRange({ from: filters.date_from, to: filters.date_to });
    setPage(0);
    load(params);
  };
  const openCreate = () => {
    setDialogMode('create');
    setEditingPromo(null);
    setDialogOpen(true);
  };
  const openEdit = (p: Promo) => {
    setDialogMode('edit');
    setEditingPromo(p);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPromo(null);
  };

  const confirmToggle = async () => {
    if (!confirmPromo) return;
    const p = confirmPromo;
    const next = Number(p.is_active) === 1 ? 0 : 1;
    setToggling(true);
    try {
      const res = await setPromoStatusAPI(p.promo_id, { is_active: next, modified_by: getAdminId() });
      if (res?.status === 200) {
        setSnack({
          open: true,
          msg: res?.message || (next === 1 ? 'Promo activated' : 'Promo deactivated'),
          sev: 'success',
        });
        setConfirmPromo(null);
        load(appliedParams);
      } else {
        setSnack({ open: true, msg: res?.message || 'Failed to update status', sev: 'error' });
      }
    } catch (err) {
      const { message } = extractApiError(err);
      setSnack({ open: true, msg: message, sev: 'error' });
    } finally {
      setToggling(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
  };

  const visible = promos.filter((p) => overlapsRange(p, appliedRange.from, appliedRange.to));
  const paged = visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const confirmActive = confirmPromo ? Number(confirmPromo.is_active) === 1 : false;

  return (
    <>
      <Box className="headingBtnWrap">
        <Typography variant="h1">Promo Code</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          style={{ backgroundColor: '#272727', color: '#fff', padding: '8px 14px', fontWeight: 'bold' }}
          onClick={openCreate}
        >
          New
        </Button>
      </Box>

      <Box className="searchInputWrap">
        <Box
          display="flex"
          alignItems="flex-end"
          gap={2}
          p={2}
          className="searchInputFilterBox"
          sx={{ width: '100%', flexWrap: 'wrap' }}
        >
          <div className="form-control inputFieldWrap" style={{ minWidth: 240 }}>
            <label>Promo Code</label>
            <input
              type="text"
              className="inputField"
              placeholder="Exact code, e.g. SUMMER10"
              value={filters.promo_code}
              onChange={(e) => setFilters((f) => ({ ...f, promo_code: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
          </div>
          <div className="form-control inputFieldWrap">
            <label>Start Date</label>
            <input
              type="date"
              className="inputField"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            />
          </div>
          <div className="form-control inputFieldWrap">
            <label>End Date</label>
            <input
              type="date"
              className="inputField"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            />
          </div>
          <Button
            variant="contained"
            onClick={onSearch}
            sx={{
              height: 40,
              px: 4,
              backgroundColor: '#272727',
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#111' },
            }}
          >
            Search
          </Button>
        </Box>
      </Box>

      <Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Promo Code</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Min Price</TableCell>
                <TableCell>Max Discount</TableCell>
                <TableCell>Validity</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
                <TableCell align="center">Details</TableCell>
                <TableCell align="center">Audit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLS} align="center" style={{ padding: 40 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={COLS} align="center" style={{ padding: 40, color: '#e31a24' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLS} align="center" style={{ padding: 40 }}>
                    No promo codes found.
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((p) => (
                  <PromoRow key={p.promo_id} promo={p} onEdit={openEdit} onToggle={setConfirmPromo} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && !error && visible.length > 0 && (
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Box>

      <PromoFormDialog
        open={dialogOpen}
        mode={dialogMode}
        promo={editingPromo}
        onClose={closeDialog}
        onSaved={(msg) => {
          closeDialog();
          setSnack({ open: true, msg, sev: 'success' });
          load(appliedParams);
        }}
      />

      <Dialog
        open={!!confirmPromo}
        onClose={toggling ? undefined : () => setConfirmPromo(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{confirmActive ? 'Deactivate promo?' : 'Activate promo?'}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            {confirmActive
              ? `Deactivating "${confirmPromo?.promo_code}" immediately blocks it from being redeemed at checkout.`
              : `Activating "${confirmPromo?.promo_code}" makes it redeemable within its validity window.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPromo(null)} variant="outlined" disabled={toggling}>
            Cancel
          </Button>
          <Button
            onClick={confirmToggle}
            variant="contained"
            disabled={toggling}
            startIcon={toggling ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ backgroundColor: confirmActive ? '#e31a24' : '#1cad2d', color: '#fff' }}
          >
            {confirmActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
